<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AccessProfileFormModal from './AccessProfileFormModal.vue';
import AccessProfilePermissionsGrid from './AccessProfilePermissionsGrid.vue';
import {
  extractConversationActions,
  extractPagePermissions,
} from './accessProfilesCatalog';

const props = defineProps({
  agents: { type: Array, default: () => [] },
});

const COPY = {
  profilesCardTitle: 'Perfis cadastrados',
  searchPlaceholder: 'Pesquisar perfil',
  newProfile: 'Novo perfil',
  emptyProfiles: 'Nenhum perfil cadastrado.',
  colProfile: 'Perfil',
  colUsers: 'Usuários',
  detailCardTitle: 'Perfil de acesso',
  newDetailCardTitle: 'Novo perfil de acesso',
  editDetailCardTitle: 'Editar perfil de acesso',
  modalDescription:
    'Defina o nome, a descrição e o que este perfil pode acessar.',
  permissionsTitle: 'Permissões',
  permissionsDescription:
    'Escolha um módulo e marque as páginas e ações liberadas para este perfil.',
  emptyDetails: 'Selecione um perfil na lista para ver os detalhes.',
  nameLabel: 'Nome do perfil',
  namePlaceholder: 'Ex.: Vendedor comercial',
  baseProfileLabel: 'Basear em',
  noBaseProfile: 'Nenhum (tudo desmarcado)',
  descriptionLabel: 'Descrição (opcional)',
  descriptionPlaceholder: 'Explique para que este perfil será usado.',
  noDescription: 'Sem descrição.',
  usersLabel: 'Usuários com este perfil',
  conversationsLabel: 'Acesso aos atendimentos',
  modulesLabel: 'Módulos',
  pagesLabel: 'Páginas / Funcionalidades',
  actionsLabel: 'Ações permitidas',
  cancel: 'Cancelar',
  edit: 'Editar',
  save: 'Salvar perfil',
  saving: 'Salvando...',
  delete: 'Excluir',
  deleteTitle: 'Excluir perfil de acesso',
  ownerNotice: 'Acesso total: não pode ser alterado.',
  standard: 'Padrão',
  customized: 'Personalizado',
  moveUsers: 'Mover usuários para',
  restoreTitle: 'Restaurar permissões padrão',
  restore: 'Restaurar padrão',
  deleteMessage: 'Tem certeza de que deseja excluir este perfil?',
  deleteConfirm: 'Sim, excluir',
  deleteReject: 'Cancelar',
  requiredFields: 'Informe o nome do perfil.',
  assignedDeleteError: 'Remova este perfil dos usuários antes de excluí-lo.',
  createSuccess: 'Perfil criado com sucesso.',
  updateSuccess: 'Perfil atualizado com sucesso.',
  deleteSuccess: 'Perfil excluído com sucesso.',
  apiError: 'Não foi possível salvar o perfil. Tente novamente.',
};

const store = useStore();
const profiles = useMapGetter('customRole/getCustomRoles');

const searchQuery = ref('');
const selectedProfileId = ref(null);
const deleteDialog = ref(null);
const restoreDialog = ref(null);
const replacementProfileId = ref('');

// Estado do modal. `showFormModal` decide se ele existe; `editingProfile`
// decide o modo: `null` cria, um objeto edita.
const showFormModal = ref(false);
const editingProfile = ref(null);

// O modulo escolhido aqui e so navegacao da LEITURA — qual das colunas de
// paginas/acoes o cartao mostra. Nao altera nada no perfil.
const selectedModuleId = ref('conversations');

const orderedProfiles = computed(() =>
  [...profiles.value].sort((a, b) => {
    const order = ['proprietario', 'gestor', 'vendedor'];
    const rank = profile =>
      order.includes(profile.system_key)
        ? order.indexOf(profile.system_key)
        : order.length;
    return rank(a) - rank(b);
  })
);

const filteredProfiles = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  if (!query) return orderedProfiles.value;
  return orderedProfiles.value.filter(profile =>
    [profile.name, profile.description]
      .filter(Boolean)
      .some(value => value.toLocaleLowerCase().includes(query))
  );
});

const selectedProfile = computed(() =>
  profiles.value.find(profile => profile.id === selectedProfileId.value)
);

const selectedProfileUserCount = computed(
  () =>
    props.agents.filter(
      agent => Number(agent.custom_role_id) === Number(selectedProfileId.value)
    ).length
);

// Permissoes do perfil escolhido, so para desenhar a grade em leitura.
const viewPermissions = computed(
  () => selectedProfile.value?.permissions || []
);
const viewConversationActions = computed(() =>
  extractConversationActions(viewPermissions.value)
);
const viewPagePermissions = computed(() =>
  extractPagePermissions(viewPermissions.value)
);

const userCountNumber = profileId =>
  props.agents.filter(
    agent => Number(agent.custom_role_id) === Number(profileId)
  ).length;

const userCount = profileId => {
  const count = props.agents.filter(
    agent => Number(agent.custom_role_id) === Number(profileId)
  ).length;
  return count === 1 ? '1 usuário' : `${count} usuários`;
};

const selectProfile = profile => {
  selectedProfileId.value = profile.id;
  selectedModuleId.value = 'conversations';
};

const selectModule = module => {
  selectedModuleId.value = module.id;
};

const startNewProfile = () => {
  editingProfile.value = null;
  showFormModal.value = true;
};

const editSelectedProfile = () => {
  if (selectedProfile.value?.immutable) return;
  if (!selectedProfile.value) return;
  editingProfile.value = selectedProfile.value;
  showFormModal.value = true;
};

const closeFormModal = () => {
  showFormModal.value = false;
  editingProfile.value = null;
};

// Depois de salvar, o perfil gravado passa a ser o que o cartao mostra —
// tanto na criacao como na edicao.
const onProfileSaved = savedProfile => {
  if (savedProfile?.id) selectedProfileId.value = savedProfile.id;
};

const replacementProfiles = computed(() =>
  orderedProfiles.value.filter(
    profile => profile.id !== selectedProfileId.value
  )
);
const requestDelete = () => {
  if (selectedProfile.value?.is_system || selectedProfile.value?.system_key)
    return;
  replacementProfileId.value =
    replacementProfiles.value.find(profile => profile.system_key === 'vendedor')
      ?.id || '';
  deleteDialog.value.open();
};

const deleteProfile = async () => {
  try {
    await store.dispatch('customRole/deleteCustomRole', {
      id: selectedProfileId.value,
      replacementCustomRoleId: selectedProfileUserCount.value
        ? replacementProfileId.value
        : null,
    });
    deleteDialog.value.close();
    await store.dispatch('agents/get');
    selectedProfileId.value = orderedProfiles.value[0]?.id || null;
    useAlert(COPY.deleteSuccess);
  } catch (error) {
    useAlert(error?.message || COPY.apiError);
  }
};

const restoreProfile = async () => {
  try {
    await store.dispatch('customRole/restoreDefault', selectedProfileId.value);
    restoreDialog.value.close();
    useAlert('Permissões padrão restauradas.');
  } catch (error) {
    useAlert(error?.message || COPY.apiError);
  }
};
const requestRestore = () => {
  if (!selectedProfile.value?.system_key || selectedProfile.value.immutable)
    return;
  restoreDialog.value.open();
};

watch(
  profiles,
  records => {
    if (!selectedProfile.value && records.length) {
      selectProfile(records[0]);
    }
  },
  { immediate: true }
);

onMounted(async () => {
  if (!profiles.value.length) await store.dispatch('customRole/getCustomRole');
});
</script>

<template>
  <div
    class="agents-page__profiles min-w-0 max-md:-mx-2 max-md:w-[calc(100%+1rem)]"
  >
    <div
      class="agents-page__profiles-grid grid items-stretch gap-6 max-md:gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)]"
    >
      <section
        class="agents-page__profiles-list-card flex min-h-0 flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-6 max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:items-center max-md:rounded-xl max-md:!p-4"
      >
        <h2
          class="agents-page__profiles-card-title mb-5 text-heading-2 text-n-slate-12 max-md:col-start-1 max-md:row-start-1 max-md:mb-0"
        >
          {{ COPY.profilesCardTitle }}
        </h2>

        <div
          class="agents-page__profiles-toolbar mb-5 flex items-center gap-3 max-md:contents"
        >
          <div
            class="agents-page__profiles-search-wrapper relative min-w-0 flex-1 max-md:col-span-2 max-md:row-start-2 max-md:mt-5 max-md:w-full"
          >
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-10 ltr:left-3 rtl:right-3"
            />
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="COPY.searchPlaceholder"
              class="agents-page__profiles-search !m-0 h-10 w-full rounded-xl border border-n-weak bg-n-solid-1 text-body-main text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:border-n-brand ltr:!pl-9 ltr:!pr-3 rtl:!pr-9 rtl:!pl-3"
            />
          </div>
          <button
            type="button"
            class="agents-page__new-profile inline-flex h-10 flex-shrink-0 items-center gap-2 rounded-xl border border-solid border-n-brand bg-n-brand/5 px-4 text-body-main font-medium text-n-blue-11 transition-colors hover:bg-n-brand/10 max-md:col-start-2 max-md:row-start-1"
            @click="startNewProfile"
          >
            <Icon icon="i-lucide-plus" class="size-4" />
            {{ COPY.newProfile }}
          </button>
        </div>

        <div
          class="agents-page__profiles-table-header grid grid-cols-[minmax(0,1fr)_7rem] items-center gap-3 border-b border-n-weak pb-3 ltr:pl-4 rtl:pr-4 max-md:col-span-2 max-md:row-start-3 max-md:mt-5 max-sm:grid-cols-[minmax(0,1fr)_5.75rem] max-sm:gap-2 max-sm:ltr:pl-2 max-sm:rtl:pr-2"
        >
          <span class="text-body-mini font-medium text-n-slate-11">{{
            COPY.colProfile
          }}</span>
          <span class="text-body-mini font-medium text-n-slate-11">{{
            COPY.colUsers
          }}</span>
        </div>

        <div
          class="agents-page__profiles-table-scroll relative min-h-0 flex-1 max-md:col-span-2 max-md:row-start-4 max-md:flex-none"
        >
          <div
            class="agents-page__profiles-table-body scrollbar-thin absolute inset-0 overflow-y-auto max-md:static max-md:max-h-72"
          >
            <span
              v-if="!filteredProfiles.length"
              class="agents-page__profiles-empty block py-10 text-center text-body-main text-n-slate-10"
            >
              {{ COPY.emptyProfiles }}
            </span>
            <template v-else>
              <div
                v-for="profile in filteredProfiles"
                :key="profile.id"
                role="button"
                tabindex="0"
                :aria-pressed="selectedProfileId === profile.id"
                class="agents-page__profile-row grid cursor-pointer grid-cols-[minmax(0,1fr)_7rem] items-center gap-3 border-b border-n-weak py-3.5 transition-colors ltr:border-l-2 ltr:pl-3.5 rtl:border-r-2 rtl:pr-3.5 max-sm:grid-cols-[minmax(0,1fr)_5.75rem] max-sm:gap-2 max-sm:ltr:pl-1.5 max-sm:rtl:pr-1.5"
                :class="
                  selectedProfileId === profile.id
                    ? 'bg-n-brand/5 ltr:border-l-n-brand rtl:border-r-n-brand'
                    : 'hover:bg-n-alpha-1 ltr:border-l-transparent rtl:border-r-transparent'
                "
                @click="selectProfile(profile)"
                @keydown.enter.prevent="selectProfile(profile)"
                @keydown.space.prevent="selectProfile(profile)"
              >
                <span
                  class="agents-page__profile-identity flex min-w-0 items-center gap-2.5"
                >
                  <Icon
                    icon="i-lucide-shield-check"
                    class="size-[18px] flex-shrink-0"
                    :class="
                      selectedProfileId === profile.id
                        ? 'text-n-brand'
                        : 'text-n-slate-10'
                    "
                  />
                  <span
                    class="agents-page__profile-name truncate text-body-main text-n-slate-12"
                  >
                    {{ profile.name }}
                  </span>
                  <span
                    v-if="profile.system_key"
                    class="rounded bg-n-alpha-2 px-2 py-0.5 text-xs"
                  >
                    {{ COPY.standard }}
                  </span>
                  <span
                    v-if="profile.system_key && profile.customized"
                    class="rounded bg-n-alpha-2 px-2 py-0.5 text-xs"
                  >
                    {{ COPY.customized }}
                  </span>
                </span>
                <span
                  class="agents-page__profile-users truncate text-body-main text-n-slate-11"
                >
                  {{ userCount(profile.id) }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </section>

      <section
        class="agents-page__profile-details-card scrollbar-thin flex min-h-0 flex-col overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-1 p-6 max-md:overflow-visible max-md:rounded-xl max-md:!p-4"
      >
        <h2
          class="agents-page__profiles-card-title mb-5 text-heading-2 text-n-slate-12 max-md:mb-4"
        >
          {{ COPY.detailCardTitle }}
        </h2>

        <span
          v-if="!selectedProfile"
          class="agents-page__profile-details-empty block py-10 text-center text-body-main text-n-slate-10"
        >
          {{ COPY.emptyDetails }}
        </span>

        <template v-else>
          <span
            class="agents-page__profile-field-label mb-1.5 text-body-mini font-medium text-n-slate-11"
          >
            {{ COPY.nameLabel }}
          </span>
          <p
            class="agents-page__profile-name-value mb-4 text-body-main text-n-slate-12"
          >
            {{ selectedProfile.name }}
          </p>

          <span
            class="agents-page__profile-field-label mb-1.5 text-body-mini font-medium text-n-slate-11"
          >
            {{ COPY.descriptionLabel }}
          </span>
          <p
            class="agents-page__profile-description-value mb-4 whitespace-pre-line text-body-main"
            :class="
              selectedProfile.description
                ? 'text-n-slate-12'
                : 'text-n-slate-10'
            "
          >
            {{ selectedProfile.description || COPY.noDescription }}
          </p>

          <span
            class="agents-page__profile-field-label mb-1.5 text-body-mini font-medium text-n-slate-11"
          >
            {{ COPY.usersLabel }}
          </span>
          <p
            class="agents-page__profile-users-value mb-5 text-body-main text-n-slate-12"
          >
            {{ userCount(selectedProfile.id) }}
          </p>

          <p
            v-if="selectedProfile.immutable"
            class="mb-4 text-body-main text-n-slate-11"
          >
            {{ COPY.ownerNotice }}
          </p>
          <AccessProfilePermissionsGrid
            readonly
            :selected-module-id="selectedModuleId"
            :page-permissions="viewPagePermissions"
            :conversation-actions="viewConversationActions"
            :labels="COPY"
            @select-module="selectModule"
          />

          <!-- As acoes ficam no fim do cartao: `mt-auto` empurra-as para baixo
               quando sobra altura, e `justify-end` alinha-as a direita. -->
          <!-- As acoes ficam iguais as da pagina de Utilizadores
               (`settings/agents/Index.vue`): mesmo componente, mesma variante,
               mesmas cores, a mesma largura fixa de `w-24` e a mesma ordem, com
               Excluir primeiro. Antes eram dois `<button>` escritos a mao, com
               icones, larguras conforme o texto e, no telemovel, empilhados e
               esticados de ponta a ponta — nada disto acontecia em
               Utilizadores. Dois botoes de `w-24` com `gap-3` ocupam 204px, e
               por isso cabem lado a lado mesmo num ecra de 393px: nao e preciso
               empilhar. -->
          <div
            class="agents-page__profile-details-actions mt-auto flex items-center justify-end gap-3 pt-1"
          >
            <Button
              v-if="!selectedProfile.system_key"
              variant="outline"
              color="ruby"
              size="md"
              class="agents-page__profile-delete w-24 rounded-xl bg-n-ruby-9/10"
              :label="COPY.delete"
              @click="requestDelete"
            />
            <Button
              v-if="selectedProfile.system_key && !selectedProfile.immutable"
              variant="outline"
              color="blue"
              size="md"
              label="Restaurar padrão"
              @click="requestRestore"
            />
            <Button
              v-if="!selectedProfile.immutable"
              variant="outline"
              color="blue"
              size="md"
              class="agents-page__profile-edit w-24 rounded-xl bg-n-brand/10"
              :label="COPY.edit"
              @click="editSelectedProfile"
            />
          </div>
        </template>
      </section>
    </div>

    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="COPY.deleteTitle"
      :description="
        selectedProfileUserCount
          ? `Escolha para qual perfil mover os ${selectedProfileUserCount} usuários antes de excluir.`
          : COPY.deleteMessage
      "
      :confirm-button-label="COPY.deleteConfirm"
      :cancel-button-label="COPY.cancel"
      :disable-confirm-button="
        selectedProfileUserCount > 0 && !replacementProfileId
      "
      @confirm="deleteProfile"
    >
      <label
        v-if="selectedProfileUserCount"
        class="block text-sm text-n-slate-12"
      >
        {{ COPY.moveUsers }}
        <select
          v-model="replacementProfileId"
          class="mt-2 w-full rounded border border-n-weak bg-n-solid-1 p-2"
        >
          <option
            v-for="profile in replacementProfiles"
            :key="profile.id"
            :value="profile.id"
          >
            {{ profile.name }}
          </option>
        </select>
      </label>
    </Dialog>
    <Dialog
      ref="restoreDialog"
      type="alert"
      :title="COPY.restoreTitle"
      :description="`Isso altera o acesso de ${selectedProfileUserCount} usuários. Deseja restaurar as permissões originais?`"
      :confirm-button-label="COPY.restore"
      :cancel-button-label="COPY.cancel"
      @confirm="restoreProfile"
    />

    <AccessProfileFormModal
      v-if="showFormModal"
      :profile="editingProfile"
      :profiles="profiles"
      :user-count="editingProfile ? userCountNumber(editingProfile.id) : 0"
      :copy="COPY"
      @close="closeFormModal"
      @saved="onProfileSaved"
    />
  </div>
</template>
